import { Action, type GetCaseRecordResponse } from '@compass/client/types/case_record';
import {
  type GenericTask,
  GenericTaskStatus,
  GenericTaskType,
} from '@compass/client/types/generic_task';
import { useCallback, useEffect, useState } from 'react';

import { useExecuteAction } from '@/hooks/useCases';
import { useCreateGenericTask, useGenericTask, useGenericTasks } from '@/hooks/useGenericTask';
import { apiClient } from '@/services/api.config';
import { useStore } from '@/store/index';
import { executeCaseAction } from '@/utils/caseActions';

export interface UsePortalSessionOptions {
  caseId: string;
  caseDetails: GetCaseRecordResponse | undefined;
}

export interface UsePortalSessionReturn {
  liveViewUrl: string | null;
  isStarting: boolean;
  isActive: boolean;
  isFillStarted: boolean;
  isFormFilled: boolean;
  isTimedOut: boolean;
  isSubmitted: boolean;
  isRecovering: boolean;
  expiresAt: string | null;
  error: string | null;
  startSession: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readOutputString(output: Record<string, unknown> | undefined, key: string): string | null {
  const val = output?.[key];
  return typeof val === 'string' && val.length > 0 ? val : null;
}

const MIN_RECOVERY_TIME_MS = 60_000;
const PENDING_TASK_ID = 'pending';

/** True when activeTaskId is a real server-assigned ID (not the pending sentinel). */
function isRealTaskId(id: string | null): id is string {
  return id !== null && id !== PENDING_TASK_ID;
}

interface DerivedState {
  liveViewUrl: string | null;
  isStarting: boolean;
  isActive: boolean;
  isFillStarted: boolean;
  isFormFilled: boolean;
  isTimedOut: boolean;
  isSubmitted: boolean;
  isTerminal: boolean;
  expiresAt: string | null;
  error: string | null;
}

interface TaskFields {
  liveViewUrl: string | null;
  sessionExpiresAt: string | null;
  fillStarted: boolean;
  formFilled: boolean;
  ackId: string | null;
  taskStatus: GenericTaskStatus | undefined;
  taskError: string | null;
}

function extractTaskFields(task: GenericTask | undefined): TaskFields {
  const output = task?.output as Record<string, unknown> | undefined;
  return {
    liveViewUrl: readOutputString(output, 'live_view_url'),
    sessionExpiresAt: readOutputString(output, 'expires_at'),
    fillStarted: !!output?.fill_started,
    formFilled: !!output?.form_filled,
    ackId: readOutputString(output, 'ack_id'),
    taskStatus: task?.status,
    taskError: task?.error || null,
  };
}

function deriveStatusFlags(taskStatus: GenericTaskStatus | undefined) {
  const isFailed = taskStatus === GenericTaskStatus.GENERIC_TASK_STATUS_FAILED;
  return {
    isInProgress: taskStatus === GenericTaskStatus.GENERIC_TASK_STATUS_IN_PROGRESS,
    isFailed,
    isTerminal: isFailed || taskStatus === GenericTaskStatus.GENERIC_TASK_STATUS_COMPLETED,
  };
}

function deriveLifecycleFlags(f: TaskFields, isInProgress: boolean, isFailed: boolean) {
  const hasUrl = Boolean(f.liveViewUrl);
  const isActive = hasUrl && isInProgress;
  // fill_started flips once the agent has logged in and the bot begins filling; until then
  // the session is in the interactive "log in" phase.
  const isFillStarted = isActive && f.fillStarted;
  const isFormFilled = isActive && f.formFilled;
  // Only treat as timeout/expiry if the session actually started (had a live_view_url).
  // A failure without a URL means the session never started (e.g. Browserbase billing error).
  const isTimedOut = isFailed && hasUrl;
  return {
    isActive,
    isFillStarted,
    isFormFilled,
    isTimedOut,
    isSubmitted: Boolean(f.ackId),
  };
}

function deriveError(isFailed: boolean, taskError: string | null, startError: string | null) {
  if (startError) return startError;
  if (isFailed) return taskError || 'Portal session failed';
  return null;
}

function deriveSessionState(
  task: GenericTask | undefined,
  activeTaskId: string | null,
  startError: string | null,
  caseDetails: GetCaseRecordResponse | undefined,
): DerivedState {
  const f = extractTaskFields(task);
  const { isInProgress, isFailed, isTerminal } = deriveStatusFlags(f.taskStatus);
  const lifecycle = deriveLifecycleFlags(f, isInProgress, isFailed);

  return {
    liveViewUrl: f.liveViewUrl,
    isStarting: Boolean(activeTaskId) && !f.liveViewUrl && !isTerminal,
    isActive: lifecycle.isActive,
    isFillStarted: lifecycle.isFillStarted,
    isFormFilled: lifecycle.isFormFilled,
    isTimedOut: lifecycle.isTimedOut,
    isSubmitted: lifecycle.isSubmitted || Boolean(caseDetails?.output_data?.data?.ack_id),
    isTerminal,
    expiresAt: lifecycle.isFormFilled ? f.sessionExpiresAt : null,
    error: deriveError(isFailed, f.taskError, startError),
  };
}

// ---------------------------------------------------------------------------
// Main hook
// ---------------------------------------------------------------------------

/**
 * Manages portal session lifecycle via generic tasks + Temporal signals.
 *
 * State model: two pieces of state (`activeTaskId`, `startError`), everything
 * else derived from the task query data and the recovery query.
 */
export function usePortalSession({
  caseId,
  caseDetails,
}: UsePortalSessionOptions): UsePortalSessionReturn {
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const executeAction = useExecuteAction();
  const { getSelectedWorkflow } = useStore();
  const selectedWorkflow = getSelectedWorkflow();
  const createGenericTask = useCreateGenericTask();
  const orgId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);

  // -- Session recovery on mount ------------------------------------------------
  const isRecovering = useSessionRecovery(
    caseId,
    caseDetails,
    activeTaskId,
    orgId ?? undefined,
    programId ?? undefined,
    setActiveTaskId,
  );

  // -- Task polling -------------------------------------------------------------
  const [pollInterval, setPollInterval] = useState<number | false>(false);
  const realTaskId = isRealTaskId(activeTaskId) ? activeTaskId : null;
  const { data: taskData } = useGenericTask(caseId, realTaskId, {
    refetchInterval: pollInterval,
  });

  const state = deriveSessionState(taskData?.task, activeTaskId, startError, caseDetails);

  // -- Update poll interval from derived state ----------------------------------
  useEffect(() => {
    if (!activeTaskId || state.isTerminal) {
      setPollInterval(false);
    } else if (state.isStarting || state.isFormFilled) {
      // Fast poll while starting (waiting for live_view_url) and after form
      // is filled (submission is imminent — minimises the gap between
      // Browserbase closing and the UI showing SubmissionCompleteView).
      setPollInterval(1000);
    } else {
      setPollInterval(3000);
    }
  }, [activeTaskId, state.isTerminal, state.isStarting, state.isFormFilled]);

  // -- Debug logging ------------------------------------------------------------
  usePortalDebugLog(caseId, activeTaskId, state);

  // -- Start session ------------------------------------------------------------
  const startSession = useStartSession(
    caseId,
    state.isStarting,
    createGenericTask,
    selectedWorkflow?.workflow_type,
    executeAction.mutate,
    setActiveTaskId,
    setStartError,
  );

  return {
    liveViewUrl: state.liveViewUrl,
    isStarting: state.isStarting,
    isActive: state.isActive,
    isFillStarted: state.isFillStarted,
    isFormFilled: state.isFormFilled,
    isTimedOut: state.isTimedOut,
    isSubmitted: state.isSubmitted,
    isRecovering,
    expiresAt: state.expiresAt,
    error: state.error,
    startSession,
  };
}

// ---------------------------------------------------------------------------
// Start session hook — extracted to reduce main hook complexity
// ---------------------------------------------------------------------------

function useStartSession(
  caseId: string,
  isStarting: boolean,
  createGenericTask: ReturnType<typeof useCreateGenericTask>,
  workflowType: string | undefined,
  executeMutate: ReturnType<typeof useExecuteAction>['mutate'],
  setActiveTaskId: (id: string | null) => void,
  setStartError: (err: string | null) => void,
) {
  return useCallback(async () => {
    if (!caseId || isStarting) return;
    // Set a sentinel value immediately so isStarting becomes true before
    // the async createGenericTask call resolves (eliminates the visual gap
    // between clicking the button and seeing the loading spinner).
    setActiveTaskId(PENDING_TASK_ID);
    setStartError(null);

    try {
      const response = await createGenericTask.mutateAsync({
        caseRecordId: caseId,
        data: { type: GenericTaskType.GENERIC_TASK_TYPE_PQC_PORTAL_FILLING_TASK },
      });

      const taskId = response.id;
      if (!taskId) throw new Error('Failed to create portal task — no ID returned');

      setActiveTaskId(taskId);

      executeCaseAction(
        'start_portal_session',
        caseId,
        workflowType,
        executeMutate,
        {
          onError: (err: any) => {
            setStartError(err?.message || 'Failed to start portal session');
          },
        },
        { task_id: taskId },
      );
    } catch (err: any) {
      setActiveTaskId(null);
      setStartError(err?.message || 'Failed to create portal task');
    }
  }, [
    caseId,
    isStarting,
    createGenericTask,
    workflowType,
    executeMutate,
    setActiveTaskId,
    setStartError,
  ]);
}

// ---------------------------------------------------------------------------
// Debug logging
// ---------------------------------------------------------------------------

function usePortalDebugLog(caseId: string, activeTaskId: string | null, state: DerivedState) {
  useEffect(() => {
    if (!activeTaskId || process.env.NODE_ENV !== 'development') return;
    console.debug('[portal-session]', { caseId, activeTaskId, ...state });
  }, [caseId, activeTaskId, state]);
}

// ---------------------------------------------------------------------------
// Session recovery
// ---------------------------------------------------------------------------

/**
 * On mount, query existing portal tasks. If a non-terminal task exists with
 * enough remaining time, set it as the active task. Otherwise close it.
 *
 * Returns true while the recovery check is in progress.
 * Uses useState (not useRef) so that completing recovery triggers a re-render.
 */
function useSessionRecovery(
  caseId: string,
  caseDetails: GetCaseRecordResponse | undefined,
  activeTaskId: string | null,
  orgId: string | undefined,
  programId: string | undefined,
  setActiveTaskId: (id: string | null) => void,
): boolean {
  const { data: recoveryData, isLoading } = useGenericTasks(caseId, {
    type: GenericTaskType.GENERIC_TASK_TYPE_PQC_PORTAL_FILLING_TASK,
    limit: 5,
  });

  const [recoveryDone, setRecoveryDone] = useState(false);

  useEffect(() => {
    if (recoveryDone || activeTaskId) return;
    if (!recoveryData) return;

    setRecoveryDone(true);
    if (recoveryData.tasks?.length) {
      attemptRecovery(recoveryData.tasks, caseDetails, caseId, orgId, programId, setActiveTaskId);
    }
  }, [
    recoveryData,
    recoveryDone,
    activeTaskId,
    caseDetails,
    caseId,
    orgId,
    programId,
    setActiveTaskId,
  ]);

  return isLoading || !recoveryDone;
}

function isSessionExpired(task: GenericTask): boolean {
  const output = task.output as Record<string, unknown> | undefined;
  const expiresAt = readOutputString(output, 'expires_at');
  return !!expiresAt && new Date(expiresAt).getTime() - Date.now() < MIN_RECOVERY_TIME_MS;
}

function findRecoverableTask(tasks: GenericTask[]): GenericTask | undefined {
  return tasks.find(
    (t: GenericTask) => t.status === GenericTaskStatus.GENERIC_TASK_STATUS_IN_PROGRESS,
  );
}

function attemptRecovery(
  tasks: GenericTask[],
  caseDetails: GetCaseRecordResponse | undefined,
  caseId: string,
  orgId: string | undefined,
  programId: string | undefined,
  setActiveTaskId: (id: string | null) => void,
) {
  if (caseDetails?.output_data?.data?.ack_id) return;

  const task = findRecoverableTask(tasks);
  if (!task?.id) return;

  if (isSessionExpired(task)) {
    if (orgId && programId) fireCloseRequest(orgId, programId, caseId);
    return;
  }

  setActiveTaskId(task.id);
}

// ---------------------------------------------------------------------------
// Portal close beacon
// ---------------------------------------------------------------------------

function fireCloseRequest(orgId: string, programId: string, caseId: string) {
  apiClient
    .post(
      `/organizations/${orgId}/programs/${programId}/caserecords/${caseId}/manual/actions/portal-session`,
      { action: Action.ACTION_PQC_CLOSE_PORTAL_SESSION },
    )
    .catch(() => {});
}

export function useClosePortalSession(caseId: string | undefined) {
  const orgId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);

  return useCallback(() => {
    if (!caseId || !orgId || !programId) return;
    fireCloseRequest(orgId, programId, caseId);
  }, [caseId, orgId, programId]);
}
